import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, PlusIcon } from 'lucide-react';
import { useCartActions } from '../../contexts/StoreContext';
import { shopLabels } from '../../data/shopLabels';
import { ease } from '../../core/format/motion';
import { isSoldOut } from '../../core/rules/stock';
import { btnAddRound } from '../../ui/styles';
import type { Product } from '../../core/types/catalog';

/** Zo lang staat het vinkje (gelijk aan de koopknop op de productpagina). */
const ADDED_MS = 2200;
type Props = {
  product: Product;
  /** Zonder melding onderin (de mand staat al open, of het bedrag spreekt voor zich). */
  silent?: boolean;
  className?: string;
};

/**
 * Eén ronde "+" voor elke plek waar je een los product toevoegt: klachtenkiezer, journal, mand.
 * Na een tik draait het plusje weg en verschijnt een vinkje, zodat je ook zonder melding ziet dat
 * het gelukt is; een schermlezer hoort het via de statusregel. Uitverkocht: geen knop.
 */
export function AddButton({
  product,
  silent,
  className = ''
}: Props) {
  const {
    add
  } = useCartActions();
  const reduce = useReducedMotion();
  const [added, setAdded] = useState(false);
  useEffect(() => {
    if (!added) return;
    const id = window.setTimeout(() => setAdded(false), ADDED_MS);
    return () => window.clearTimeout(id);
  }, [added]);
  if (isSoldOut(product)) return null;
  const icon = reduce ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      opacity: 0,
      rotate: -90,
      scale: 0.96
    },
    animate: {
      opacity: 1,
      rotate: 0,
      scale: 1
    },
    exit: {
      opacity: 0,
      rotate: 90,
      scale: 0.96
    }
  };
  return <>
      <button type="button" onClick={() => {
      if (add(product, silent ? {
        silent: true
      } : undefined)) setAdded(true);
    }} aria-label={shopLabels.addToCart(product)} className={`${btnAddRound(added)} ${className}`} data-id="element-8815" data-mp="5dat1yajm5uk">
        <AnimatePresence mode="wait" initial={false} data-id="element-8816" data-mp="2q5mgme9vid72">
          <motion.span key={added ? 'check' : 'plus'} {...icon} transition={{
          duration: 0.16,
          ease
        }} className="flex" data-id="element-8817" data-mp="1i3t6cannweyf">
            {added ? <CheckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-8818" data-mp="1m54o5u0anvw2" /> : <PlusIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8819" data-mp="184tukj7lo377" />}
          </motion.span>
        </AnimatePresence>
      </button>
      <span className="sr-only" role="status" data-id="element-8820" data-mp="1d72pvm7y3hch">
        {added ? `${product.name} ${shopLabels.addedToCart}` : ''}
      </span>
    </>;
}